import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    question: "What are the benefits of magnetic copper?",
    answer: "Copper has been used for millennia for its natural properties. Our pure copper accessories integrate therapeutic magnets that may help improve blood circulation, reduce tension, and promote a general sense of well-being. Many of our customers report feeling calm and balanced in their daily lives.",
  },
  {
    question: "How do I choose my ring size?",
    answer: "Our Calmi Ring™ rings are adjustable and fit most finger sizes. However, for optimal fit, we recommend measuring your finger circumference. You can use a measuring tape or string, then compare with our size guide. If in doubt, our customer service team is available to help.",
  },
  {
    question: "How do I care for my copper jewelry?",
    answer: "Copper naturally develops a patina over time, which is completely normal. To maintain the shine of your jewelry, clean it regularly with a soft, dry cloth. Avoid prolonged contact with water, perfumes, and chemicals. For deep cleaning, use a mixture of lemon and salt, then rinse and dry thoroughly.",
  },
  {
    question: "Can copper cause allergic reactions?",
    answer: "Pure copper is generally well-tolerated by skin. However, some sensitive individuals may notice slight green discoloration on the skin, which is a natural and harmless reaction. This discoloration disappears with simple washing. If you have known metal allergies, consult your doctor before use.",
  },
  {
    question: "How long does delivery take?",
    answer: "We ship your orders within 24-48 business hours. Standard delivery takes approximately 10 business days. All our shipments are tracked and you will receive a tracking number as soon as your package is sent. Free shipping on orders over $50.",
  },
  {
    question: "Can I return my product if I'm not satisfied?",
    answer: "Absolutely! We offer a 30-day satisfaction guarantee. If for any reason you are not completely satisfied with your purchase, you can return the product in its original condition. Returns are free and we will provide a full refund.",
  },
];

// Generate FAQ Schema JSON-LD for Rich Snippets
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map((faq) => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer,
    },
  })),
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

export const FAQSection = () => {
  return (
    <section className="section-padding bg-background">
      {/* FAQ Rich Snippets Schema */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="container-calim">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          variants={fadeInUp}
          className="text-center mb-10 md:mb-12"
        >
          <p className="text-subhead mb-3 md:mb-4">FAQ</p>
          <h2 className="text-headline">Frequently Asked Questions</h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
          variants={fadeInUp}
          className="max-w-3xl mx-auto"
        >
          <Accordion type="single" collapsible className="w-full space-y-3 md:space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border border-border/50 rounded-lg px-4 md:px-6 data-[state=open]:bg-secondary/30"
              >
                <AccordionTrigger className="text-left font-semibold tracking-wide hover:no-underline py-4 md:py-5 text-sm md:text-base">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-4 md:pb-5 leading-relaxed text-sm md:text-[15px]">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};
